import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useStatsigClient } from '@statsig/react-bindings';

import LogRocket from 'logrocket';

import {
  ERROR_SAVE_DRAFT,
  LINEAR_PREBUILD_SURVEY_TYPES,
  QUESTION_TYPES,
  RESEARCH_OBJECTIVES_MAX_LENGTH,
  SURVEY_TYPES,
} from '@api/instant_survey';

import type { SamplePricingOption } from '@api/surveys/types';
import {
  shareInstantSurveyWithClientGroup,
  shareInstantSurveyWithUsers,
  surveyStatus,
} from '@api/surveys';

import { GATES } from '@constants';
import { canPauseSurvey, canResumeSurvey } from '@helpers/surveys';
import {
  ButtonText,
  Card,
  ColumnsIcon,
  HelpIcon,
  Input,
  MinusIcon,
  OverlayTriggerTooltip,
  Pill,
  PlusIcon,
  RepromptIcon,
  SaveIcon,
  ShareIcon,
  Stepper,
  TextArea,
  toast,
  Toolbar,
} from '@utilities';

import { getCurrentSamplePrice } from '../../utilities/helpers';
import { payWithCompletes, payWithCredits } from '../../../Checkout/utilities/constants';

import ModalSaveAsDraft from '../ModalSaveAsDraft';
import ModalShare from '../../../../components/ModalShare';
import ModalTitleRequired from '../ModalTitleRequired';
import PauseModal from '../../../Workspace/pages/Surveys/components/Pause/components/PauseModal';
import ResumeModal from '../../../Workspace/pages/Surveys/components/Resume/components/ResumeModal';
import { useSurveyContext } from '../../utilities/context';

import ModalLaunchMethodType from './components/ModalLaunchMethodType';

import styles from './_index.module.scss';

interface SurveyToolbarProps {
  isSaveAsDraftDisabled?: boolean;
  tools?: Array<React.ReactNode>;
}

const SurveyToolbar = ({ isSaveAsDraftDisabled, tools = [] }: SurveyToolbarProps) => {
  const {
    activeGroups,
    activeQuestions,
    activeStep,
    currentQuestionCount,
    currentQuestionnairePrice,
    insightsDocument,
    insightsDocumentType,
    instantSurveyType,
    isAutoSaving,
    isLoadingSubscription,
    isReadOnly,
    isResearchObjectivesTooLong,
    isSaveAsDraftOpen,
    isSavingAsDraft,
    isSubscribed,
    launchMethod,
    maxQuestions,
    persistSurveyDraft,
    setActiveStep,
    setInsightsDocument,
    setIsSaveAsDraftOpen,
    setIsSavingAsDraft,
    setLaunchMethod,
    subscription,
    subscriptionError,
    survey,
    updateSurvey,
  } = useSurveyContext();
  const [isLaunchMethodModalOpen, setIsLaunchMethodModalOpen] = useState(false);
  const [isPauseModalOpen, setIsPauseModalOpen] = useState(false);
  const [isResumeModalOpen, setIsResumeModalOpen] = useState(false);
  const [isSurveyTitleModalOpen, setIsSurveyTitleModalOpen] = useState(false);
  const [showModalShare, setShowModalShare] = useState(false);
  const [showResearchObjectiveEdit, setShowResearchObjectiveEdit] = useState(false);
  const { permissions = {} } = useSelector((state: any) => state?.user?.data);

  const { client } = useStatsigClient();
  const useCalypsoProvider = client.checkGate(GATES.USE_CALYPSO_AS_SURVEY_PROVIDER);
  const isNonLinearSurveyEnabled = client.checkGate(GATES.START_SURVEY_NON_LINEAR);

  const isLinearPrebuildSurvey = LINEAR_PREBUILD_SURVEY_TYPES.includes(instantSurveyType?.id);

  const isNonDraftSurvey =
    !!insightsDocument?.status && insightsDocument.status !== surveyStatus.DRAFT;

  const canPause = canPauseSurvey({ permissions, survey: insightsDocument, useCalypsoProvider });
  const canResume = canResumeSurvey({ permissions, survey: insightsDocument, useCalypsoProvider });

  const currentSamplePrice = useMemo<SamplePricingOption>(
    () =>
      // @ts-ignore helpers file is not typed
      getCurrentSamplePrice({
        // @ts-ignore
        samplePricing: insightsDocumentType?.samplePricing,
        survey,
      }),
    [survey]
  );

  const currentSampleCompletesPrice = useMemo<SamplePricingOption>(
    () =>
      // @ts-ignore helpers file is not typed
      getCurrentSamplePrice({
        // @ts-ignore
        samplePricing: insightsDocumentType?.sampleCompletesPricing,
        survey,
      }),
    [insightsDocumentType?.sampleCompletesPricing, survey]
  );

  const handleConfirmMethod = (method: string) => {
    setLaunchMethod(method);
    setIsLaunchMethodModalOpen(false);
  };

  const currentTotalResponses = activeGroups.reduce((acc, group) => acc + group.quotaLimit, 0);
  const hasMaxDiffQuestion = activeQuestions.some(
    ({ type }) => type === QUESTION_TYPES.MAX_DIFF.value
  );
  const hasTurfQuestion = activeQuestions.some(({ type }) => type === QUESTION_TYPES.TURF.value);
  const isVideoSurvey = instantSurveyType?.id === SURVEY_TYPES.VIDEO;
  const isSubscriptionAvailable =
    import.meta.env.VITE_LAUNCH_METHOD &&
    isSubscribed &&
    !isLoadingSubscription &&
    !subscriptionError &&
    subscription?.hasStarted === true;

  const advancedMethodologyCompletesCost = 300;
  const advancedMethodologyCreditCost = 1;
  const completesTotal =
    (isVideoSurvey ? (currentSampleCompletesPrice?.amount ?? 0) : currentTotalResponses) +
    ((currentQuestionnairePrice?.questions ?? 0) >= 16 ? 100 : 0) +
    (hasMaxDiffQuestion ? advancedMethodologyCompletesCost : 0) +
    (hasTurfQuestion ? advancedMethodologyCompletesCost : 0);

  const questionnaireCredits = isVideoSurvey ? 0 : (currentQuestionnairePrice?.amount ?? 1);
  const creditsTotal =
    (currentSamplePrice?.amount ?? 0) +
    (hasMaxDiffQuestion ? advancedMethodologyCreditCost : 0) +
    (hasTurfQuestion ? advancedMethodologyCreditCost : 0) +
    questionnaireCredits;

  const navigate = useNavigate();
  const handleOpenSaveAsDraft = () => {
    if (!survey.title.trim()) {
      setIsSurveyTitleModalOpen(true);
    } else {
      setIsSaveAsDraftOpen(true);
    }
  };
  const handlePause = () => {
    setInsightsDocument((prev) => ({ ...prev!, isPaused: true }));
    setIsPauseModalOpen(false);
  };
  const handleResume = () => {
    setInsightsDocument((prev) => ({ ...prev!, isPaused: false }));
    setIsResumeModalOpen(false);
  };
  const handleSaveAndExit = async () => {
    setIsSavingAsDraft(true);
    try {
      await persistSurveyDraft({ reason: 'manual' });
    } catch (error) {
      console.error('Error saving survey draft:', error);
      toast.error(ERROR_SAVE_DRAFT);
      setIsSavingAsDraft(false);
      return;
    }

    LogRocket.track('InstantSurvey_SaveAsDraft', {
      country: survey.country,
      groups: survey.groups.length,
      questions: survey.questions.length,
      ...(survey.surveyTemplateId && { template: survey.surveyTemplateId }),
      type: instantSurveyType.id,
    });
    setIsSavingAsDraft(false);
    navigate({
      pathname: '/dashboard/workspace/surveys',
    });
  };

  return (
    <>
      <Card className={styles['survey-toolbar']} padding="medium">
        <div>
          <Toolbar
            tools={[
              ...tools,
              ...(insightsDocument?.id && !isReadOnly
                ? [
                    <OverlayTriggerTooltip content="Share" key="share">
                      <button data-testid="share-button" onClick={() => setShowModalShare(true)}>
                        <ShareIcon />
                      </button>
                    </OverlayTriggerTooltip>,
                  ]
                : []),
              ...(!isReadOnly
                ? [
                    <OverlayTriggerTooltip
                      content={
                        isAutoSaving
                          ? 'Auto-saving in progress...'
                          : isNonDraftSurvey
                            ? 'Save and Exit'
                            : 'Save Draft and Close'
                      }
                      key="save-as-draft"
                    >
                      <button
                        data-testid="save-draft-button"
                        disabled={isSaveAsDraftDisabled || isAutoSaving}
                        onClick={handleOpenSaveAsDraft}
                      >
                        <SaveIcon />
                      </button>
                    </OverlayTriggerTooltip>,
                  ]
                : []),
              ...(canPause
                ? [
                    <OverlayTriggerTooltip content="Pause Survey" key="pause-survey">
                      <button
                        data-testid="pause-survey-button"
                        onClick={() => setIsPauseModalOpen(true)}
                      >
                        <ColumnsIcon />
                      </button>
                    </OverlayTriggerTooltip>,
                  ]
                : []),
              ...(canResume
                ? [
                    <OverlayTriggerTooltip content="Resume Survey" key="resume-survey">
                      <button
                        data-testid="resume-survey-button"
                        onClick={() => setIsResumeModalOpen(true)}
                      >
                        <RepromptIcon />
                      </button>
                    </OverlayTriggerTooltip>,
                  ]
                : []),

              ...(instantSurveyType.helpUrl
                ? [
                    <OverlayTriggerTooltip content="Help" key="help">
                      <a
                        aria-label="Help"
                        data-testid="help-link-button"
                        href={instantSurveyType.helpUrl}
                        rel="noreferrer"
                        target="_blank"
                      >
                        <HelpIcon />
                      </a>
                    </OverlayTriggerTooltip>,
                  ]
                : []),
            ]}
          >
            <Input
              className={styles['survey-toolbar-input']}
              isDisabled={isReadOnly}
              maxLength={255}
              onBlur={(title) => updateSurvey({ title: String(title).trim() }, { markDirty: true })}
              onChange={(title) => updateSurvey({ title: String(title) }, { markDirty: true })}
              placeholder="Name your survey (e.g., Heavy vs Light Walmart coffee buyers)"
              value={survey.title}
            />
          </Toolbar>
          {(!isReadOnly || survey.researchObjectives?.length > 0) && (
            <div className={styles['survey-toolbar-research-objectives']}>
              {showResearchObjectiveEdit ? (
                <div>
                  <div className={styles['survey-toolbar-research-objectives-edit']}>
                    <TextArea
                      className={styles['survey-toolbar-research-objectives-textarea']}
                      error={
                        isResearchObjectivesTooLong
                          ? `Research objective is too long, cannot exceed ${RESEARCH_OBJECTIVES_MAX_LENGTH} characters.`
                          : undefined
                      }
                      isDisabled={isReadOnly}
                      onBlur={() => {
                        updateSurvey(
                          { researchObjectives: survey.researchObjectives?.trim() },
                          { markDirty: true }
                        );
                        setShowResearchObjectiveEdit(false);
                      }}
                      onChange={(research_objectives) =>
                        updateSurvey(
                          { researchObjectives: research_objectives },
                          { markDirty: true }
                        )
                      }
                      placeholder="Enter the research objective for this survey."
                      value={survey.researchObjectives || ''}
                    />
                  </div>
                  <div>
                    <ButtonText
                      onClick={() => {
                        setShowResearchObjectiveEdit(false);
                      }}
                    >
                      <MinusIcon />
                      <span>
                        Hide <strong>Objective</strong>
                      </span>
                    </ButtonText>
                  </div>
                </div>
              ) : (
                <ButtonText onClick={() => setShowResearchObjectiveEdit(true)}>
                  <PlusIcon />
                  <span>
                    Show <strong>Objective</strong>
                  </span>
                </ButtonText>
              )}
            </div>
          )}
        </div>

        <div className={styles['survey-toolbar-info']}>
          <div>
            <p>
              <strong>{activeGroups.length}</strong> Quota Group
              {activeGroups.length > 1 ? 's' : ''}
            </p>
            <p>
              <strong>{currentTotalResponses}</strong> Total Responses
            </p>
            <p>
              <strong>{currentQuestionCount + '/' + maxQuestions}</strong>{' '}
              {`Question${maxQuestions === 1 ? '' : 's'}`}
            </p>
          </div>
          <div>
            {isSubscriptionAvailable ? (
              <>
                <button
                  className={styles['survey-toolbar-launch-method-button']}
                  disabled={isNonDraftSurvey}
                  onClick={() => setIsLaunchMethodModalOpen(true)}
                >
                  {isNonDraftSurvey ? 'Launched with' : 'Launch with'}
                </button>
                &nbsp;
                <span>
                  <strong>
                    {launchMethod === payWithCompletes.key
                      ? `${completesTotal} Completes`
                      : launchMethod === payWithCredits.key &&
                          creditsTotal !== undefined &&
                          !isNaN(creditsTotal)
                        ? `${creditsTotal} Credit${creditsTotal === 1 ? '' : 's'}`
                        : 'N/A'}
                  </strong>
                </span>
              </>
            ) : (
              <>
                Total Cost:&nbsp;
                {currentSamplePrice ? (
                  <Pill
                    isCompleted
                    value={`${creditsTotal} Credit${creditsTotal === 1 ? '' : 's'}`}
                  />
                ) : (
                  <span>N/A</span>
                )}
              </>
            )}
          </div>
        </div>
        <Stepper
          activeIndex={activeStep}
          navigationType={
            isNonLinearSurveyEnabled && !isLinearPrebuildSurvey ? 'non-linear' : 'linear'
          }
          onChange={(index) => setActiveStep(index)}
          steps={[
            {
              isComplete: activeStep > 0,
              label: 'Select Panelists',
            },
            {
              isComplete: survey.questions.length >= 1,
              label: 'Create Questionnaire',
            },
            {
              label: 'Review Survey',
            },
          ]}
        />
      </Card>
      {isPauseModalOpen && (
        <PauseModal
          id={insightsDocument!.id}
          onClose={() => setIsPauseModalOpen(false)}
          onSubmit={handlePause}
        />
      )}
      {isResumeModalOpen && (
        <ResumeModal
          id={insightsDocument!.id}
          onClose={() => setIsResumeModalOpen(false)}
          onSubmit={handleResume}
        />
      )}
      {isSaveAsDraftOpen && (
        <ModalSaveAsDraft
          isActive
          isNonDraftSurvey={isNonDraftSurvey}
          isSaving={isSavingAsDraft}
          onClose={() => setIsSaveAsDraftOpen(false)}
          onSubmit={handleSaveAndExit}
        />
      )}
      {isSurveyTitleModalOpen && (
        <ModalTitleRequired onClose={() => setIsSurveyTitleModalOpen(false)} />
      )}
      {isLaunchMethodModalOpen && (
        <ModalLaunchMethodType
          creditsTotal={creditsTotal}
          isOpen={isLaunchMethodModalOpen}
          launchMethod={launchMethod}
          onClose={() => setIsLaunchMethodModalOpen(false)}
          onConfirm={handleConfirmMethod}
          setLaunchMethod={setLaunchMethod}
        />
      )}
      {showModalShare && insightsDocument?.id && (
        // @ts-ignore ModalShare is not typed yet
        <ModalShare
          hasAllColleagues
          hasFindColleagues
          hasSharePublicly
          ids={[Number(insightsDocument.id)]}
          isSurvey
          setShowModalShare={setShowModalShare}
          shareItemWithClients={shareInstantSurveyWithClientGroup}
          shareItemWithUsers={shareInstantSurveyWithUsers}
        />
      )}
    </>
  );
};

export default SurveyToolbar;
